import './ServiceOrderShowLogistic.scss';

import { Button, Card, Col, Empty, Row, Space, Switch, Table, Tag, Typography } from 'antd';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ServiceOrderShowLogisticProps {
  serviceOrder: any;
  serviceOrderItems: ServiceOrderItem[];
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  refetch: () => Promise<void>;
}

const ServiceOrderShowLogistic = (props: ServiceOrderShowLogisticProps) => {
  const { serviceOrderRepository } = useRepositoriesContext();
  const { t } = useTranslation();
  const { serviceOrder, serviceOrderItems, setServiceOrderItems, refetch } = props;
  const { technician } = serviceOrder;
  // Items que dejarán remanente en el stock personal del técnico.
  const itemsToTechnicianStock = useMemo(
    () =>
      serviceOrderItems
        .filter(item => (item.stock_technician_amount ?? 0) > 0)
        .sort((a, b) => (b.stock_technician_amount ?? 0) - (a.stock_technician_amount ?? 0)),
    [serviceOrderItems],
  );
  // Items consumidos desde stock personal (no provienen de un envío).
  const itemsFromTechnicianStock = useMemo(
    () =>
      serviceOrderItems
        .filter(item => item.package_item == undefined && (item.used_amount ?? 0) > 0)
        .sort((a, b) => (b.used_amount ?? 0) - (a.used_amount ?? 0)),
    [serviceOrderItems],
  );
  const hasLogisticMovements =
    itemsToTechnicianStock.length > 0 || itemsFromTechnicianStock.length > 0;
  // Filas unificadas para visualizar y gestionar el estado logístico en una sola tabla.
  const itemsWithLogisticMovements = useMemo(
    () => [...itemsToTechnicianStock, ...itemsFromTechnicianStock],
    [itemsToTechnicianStock, itemsFromTechnicianStock],
  );
  const completedCount = useMemo(
    () =>
      itemsWithLogisticMovements.reduce(
        (total, row) => total + (row.stock_technician_reviewed ? 1 : 0),
        0,
      ),
    [itemsWithLogisticMovements],
  );
  const markChecked = (itemId: number, completed: boolean) => {
    setServiceOrderItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, stock_technician_reviewed: completed, toUpdate: true }
          : item,
      ),
    );
  };

  const hasChanges = useMemo(
    () => serviceOrderItems.some(item => item.toUpdate),
    [serviceOrderItems],
  );
  const { Text } = Typography;

  return (
    <div className="ServiceOrderShowLogistic">
      <Card className="ServiceOrderShowLogisticSummary" size="small">
        <div className="ServiceOrderShowLogisticSummaryHeader">
          <div>
            <div className="ServiceOrderShowLogisticSummaryTitle">{t('Resumen logístico')}</div>
            <div>
              <b>{t('Técnico asignado')}: </b>{' '}
              <LinkTextField
                label={technician?.nombre ?? '-'}
                to={`/technicians/${technician?.id}`}
                admin
                newTab
              />
            </div>
          </div>
          <Tag className="ServiceOrderShowLogisticSummaryTag" color="geekblue">
            {t('Elementos cargados')}: {serviceOrderItems.length}
          </Tag>
        </div>

        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Card className="ServiceOrderShowLogisticMetricCard" size="small">
              <Text strong>{t('A registrar en stock técnico')}</Text>
              <div className="ServiceOrderShowLogisticMetricValue">
                {itemsToTechnicianStock.length}
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="ServiceOrderShowLogisticMetricCard" size="small">
              <Text strong>{t('Consumidos de stock técnico')}</Text>
              <div className="ServiceOrderShowLogisticMetricValue">
                {itemsFromTechnicianStock.length}
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Estado vacío cuando no existe impacto logístico en la orden. */}
      {!hasLogisticMovements && (
        <Card size="small">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('Esta orden no registra movimientos logísticos para el técnico')}
          />
        </Card>
      )}

      {hasLogisticMovements && (
        <Card
          className="ServiceOrderShowLogisticSection"
          size="small"
          title={t('Detalle de movimientos logísticos')}
          extra={
            <Space size={8} wrap className="ServiceOrderShowLogisticTableToolbar">
              <Tag className="ServiceOrderShowLogisticSummaryTag" color="gold">
                {t('Realizados')}: {completedCount}/{itemsWithLogisticMovements.length}
              </Tag>
            </Space>
          }>
          <div className="ServiceOrderShowLogisticTableContainer">
            <Table<ServiceOrderItem>
              className="ServiceOrderShowLogisticTable"
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={itemsWithLogisticMovements}
              tableLayout="fixed"
              scroll={{ x: 820 }}
              locale={{ emptyText: <Empty description={t('No hay movimientos logísticos')} /> }}>
              <Table.Column<ServiceOrderItem>
                width={170}
                title={t('Movimiento')}
                render={(_: unknown, row: ServiceOrderItem) => (
                  <Tag color={row.package_item ? 'blue' : 'purple'}>
                    {row.package_item
                      ? t('Ingreso a stock técnico')
                      : t('Consumo de stock técnico')}
                  </Tag>
                )}
              />
              <Table.Column<ServiceOrderItem>
                width={280}
                title={t('Item')}
                render={(_: unknown, row: ServiceOrderItem) => (
                  <div className="ServiceOrderShowLogisticItemCell">
                    <Text strong>{row.name}</Text>
                    {row.serial_number && (
                      <Text type="secondary">
                        {t('Numero de serie')}: {row.serial_number}
                      </Text>
                    )}
                  </div>
                )}
              />
              <Table.Column<ServiceOrderItem>
                width={120}
                title={t('Cantidad')}
                render={(_: unknown, row: ServiceOrderItem) =>
                  row.package_item ? row.stock_technician_amount : row.used_amount
                }
              />
              <Table.Column<ServiceOrderItem>
                width={170}
                title={t('Origen')}
                render={(_: unknown, row: ServiceOrderItem) => (
                  <Tag color={row.package_item ? 'blue' : 'default'}>
                    {row.package_item ? t('Envío') : t('Stock técnico')}
                  </Tag>
                )}
              />
              <Table.Column<ServiceOrderItem>
                width={220}
                title={t('Estado logístico')}
                render={(_: unknown, row: ServiceOrderItem) => (
                  <Switch
                    checked={row.stock_technician_reviewed ?? false}
                    checkedChildren={t('Realizado')}
                    unCheckedChildren={t('Pendiente')}
                    onChange={checked => markChecked(row.id, checked)}
                  />
                )}
              />
            </Table>
          </div>
        </Card>
      )}
      {hasChanges && (
        <div className="ServiceOrderShowLogisticButtons">
          <Button
            type="primary"
            onClick={() => {
              void serviceOrderRepository
                .saveServiceOrderItems(serviceOrderItems, serviceOrder.id)
                .then(() => refetch());
            }}>
            {t('Guardar cambios')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ServiceOrderShowLogistic;
